import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { staffApi } from "../../lib/staff";
import { enqueueCheckin } from "../../lib/checkinQueue";
import { playAccept, playReject, playQueued } from "../../lib/sound";
import {
  Search, CheckCircle2, Loader2, UserCheck, Ticket, CalendarDays, Users, Undo2,
} from "lucide-react";

function fmtDate(ds) {
  if (!ds) return "";
  try {
    const d = new Date(ds + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch { return ds; }
}

export default function StaffSearchPage() {
  const { eventId, staff, refreshSummary } = useOutletContext();
  const device = staff?.device || "mobile-search";
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyIds, setBusyIds] = useState({});

  const runSearch = async (e) => {
    e?.preventDefault();
    const query = q.trim();
    if (query.length < 2) {
      toast.error("Type at least 2 characters to search.");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const params = { q: query };
      if (eventId) params.event_id = eventId;
      const { data } = await staffApi.get("/checkin/search", { params });
      setResults(data.results || []);
    } catch {
      toast.error("Search failed. Try again.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const setAttendee = (orderId, attId, patch) => {
    setResults((prev) => prev.map((r) => {
      if (r.order.id !== orderId) return r;
      return { ...r, attendees: r.attendees.map((a) => (a.id === attId ? { ...a, ...patch } : a)) };
    }));
  };

  const undoCheckIn = async (orderId, att) => {
    if (busyIds[att.id]) return;
    if (!window.confirm(`Undo check-in for ${att.name || "this guest"}? They can scan in again after.`)) return;
    setBusyIds((b) => ({ ...b, [att.id]: true }));
    try {
      await staffApi.post("/checkin/undo", { attendee_id: att.id, device });
      setAttendee(orderId, att.id, { checked_in: false, checked_in_at: null });
      toast.success(`${att.name || "Guest"} checked back out`);
      refreshSummary?.();
    } catch {
      toast.error("Could not undo — check your connection and try again.");
    } finally {
      setBusyIds((b) => ({ ...b, [att.id]: false }));
    }
  };

  const checkInOne = async (orderId, att) => {
    if (att.checked_in || busyIds[att.id]) return { ok: false, reason: "skip" };
    setBusyIds((b) => ({ ...b, [att.id]: true }));
    try {
      if (!navigator.onLine) {
        enqueueCheckin({ attendee_id: att.id, device });
        setAttendee(orderId, att.id, { checked_in: true });
        playQueued();
        return { ok: true, reason: "queued" };
      }
      const { data } = await staffApi.post("/checkin", { attendee_id: att.id, device, force: true });
      if (data.ok) {
        setAttendee(orderId, att.id, { checked_in: true, checked_in_at: data.attendee?.checked_in_at });
        try { navigator.vibrate?.(50); } catch { /* noop */ }
        playAccept();
        if (refreshSummary) refreshSummary();
        return { ok: true };
      }
      if (data.reason === "already_checked_in") {
        setAttendee(orderId, att.id, { checked_in: true });
        playReject();
        return { ok: true, reason: "already_checked_in" };
      }
      if (data.reason === "wrong_date") {
        playReject();
        return { ok: false, reason: "wrong_date", visit_date: data.visit_date };
      }
      playReject();
      return { ok: false, reason: data.reason || "failed" };
    } catch (err) {
      if (!err?.response) {
        enqueueCheckin({ attendee_id: att.id, device });
        setAttendee(orderId, att.id, { checked_in: true });
        playQueued();
        return { ok: true, reason: "queued" };
      }
      playReject();
      return { ok: false, reason: "error" };
    } finally {
      setBusyIds((b) => ({ ...b, [att.id]: false }));
    }
  };

  const checkInOneClick = async (orderId, att) => {
    const res = await checkInOne(orderId, att);
    if (res.reason === "queued") toast.info(`${att.name || "Guest"} saved offline — will sync`);
    else if (res.ok && res.reason === "already_checked_in") toast.info(`${att.name || "Guest"} was already checked in`);
    else if (res.reason === "wrong_date") toast.error(`${att.name || "Guest"}: ticket is for ${res.visit_date}`);
    else if (!res.ok && res.reason !== "skip") toast.error(`Could not check in ${att.name || "guest"}`);
  };

  const checkInAll = async (r) => {
    const pending = r.attendees.filter((a) => !a.checked_in && a.status !== "cancelled");
    if (pending.length === 0) return;
    let ok = 0;
    const fails = {};
    for (const a of pending) {
      // eslint-disable-next-line no-await-in-loop
      const res = await checkInOne(r.order.id, a);
      if (res.ok) ok += 1;
      else fails[res.reason] = (fails[res.reason] || 0) + 1;
    }
    const failCount = pending.length - ok;
    if (failCount === 0) {
      toast.success(`${r.order.customer_name || "Order"} — ${ok} checked in`);
    } else if (ok === 0) {
      const wd = fails.wrong_date ? " (wrong date)" : "";
      toast.error(`None checked in — ${failCount} could not be admitted${wd}`);
    } else {
      toast.warning(`${ok} checked in, ${failCount} could not be admitted`);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="staff-search-page">
      <form onSubmit={runSearch} className="p-3 border-b border-[#1a1a1a] bg-[#0c0c0c] flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8a8a]" />
          <input
            className="st-input w-full pl-9"
            placeholder="Order ID, name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="staff-search-input"
          />
        </div>
        <button type="submit" className="st-btn" disabled={loading} data-testid="staff-search-submit">
          {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
          Search
        </button>
      </form>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-12 text-[#8a8a8a]">
            <Loader2 className="animate-spin" size={22} />
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <div className="text-center py-12 text-[#8a8a8a]" data-testid="staff-search-empty">
            <Search size={32} className="mx-auto mb-3 opacity-50" />
            No matching orders found.
          </div>
        )}

        {!loading && !searched && (
          <div className="text-center py-12 text-[#6a6a6a] text-sm" data-testid="staff-search-hint">
            <UserCheck size={32} className="mx-auto mb-3 opacity-50" />
            Search for a booking to check guests in manually.
          </div>
        )}

        {!loading && results.map((r) => {
          const active = r.attendees.filter((a) => a.status !== "cancelled");
          const done = active.filter((a) => a.checked_in).length;
          const allDone = done === active.length;
          return (
            <div key={r.order.id} className="rounded-xl border border-[#1a1a1a] bg-[#0e0e0e] overflow-hidden" data-testid={`staff-order-${r.order.id}`}>
              <div className="px-3.5 py-3 border-b border-[#161616]">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold truncate">{r.order.customer_name || "Guest"}</div>
                  <span className="text-xs font-mono text-[#8a8a8a] shrink-0">{r.order.order_number}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-[#8a8a8a]">
                  <span className="flex items-center gap-1"><CalendarDays size={12} /> {fmtDate(r.event_date?.date)}</span>
                  <span className="flex items-center gap-1"><Users size={12} /> {done}/{active.length} in</span>
                </div>
                {r.order.customer_email && (
                  <div className="text-xs text-[#6a6a6a] truncate mt-0.5">{r.order.customer_email}</div>
                )}
              </div>

              <div className="divide-y divide-[#161616]">
                {active.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-2 px-3.5 py-2.5" data-testid={`staff-attendee-${a.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm truncate">{a.name || "Ticket holder"}</div>
                      <div className="text-xs text-[#8a8a8a] flex items-center gap-1 truncate">
                        <Ticket size={11} /> {a.ticket_type_name || "Ticket"}
                        {a.event_date && r.event_date?.date && a.event_date !== r.event_date.date && (
                          <span className="ml-1 text-[#e8c987] whitespace-nowrap" data-testid={`staff-attendee-moved-${a.id}`}>
                            · moved to {fmtDate(a.event_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    {a.checked_in ? (
                      <button
                        onClick={() => undoCheckIn(r.order.id, a)}
                        disabled={!!busyIds[a.id]}
                        className="flex items-center gap-1 text-xs font-semibold text-[#22c55e] shrink-0 px-2 py-1.5 rounded-lg border border-[#1f3d1f] active:bg-[#0f1a0f]"
                        data-testid={`staff-attendee-done-${a.id}`}
                      >
                        {busyIds[a.id] ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={16} />} In
                        <Undo2 size={12} className="text-[#6a6a6a]" />
                      </button>
                    ) : (
                      <button
                        onClick={() => checkInOneClick(r.order.id, a)}
                        disabled={!!busyIds[a.id]}
                        className="st-btn !px-3 !py-1.5 text-xs shrink-0"
                        data-testid={`staff-checkin-one-${a.id}`}
                      >
                        {busyIds[a.id] ? <Loader2 className="animate-spin" size={13} /> : <UserCheck size={13} />}
                        Check in
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="px-3.5 py-2.5 border-t border-[#161616]">
                <button
                  onClick={() => checkInAll(r)}
                  disabled={allDone}
                  className="st-btn w-full justify-center disabled:opacity-40"
                  data-testid={`staff-checkin-all-${r.order.id}`}
                >
                  <CheckCircle2 size={15} />
                  {allDone ? "Everyone checked in" : `Check in all (${active.length - done})`}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
